import { PlaylistEdgeTransitionFunction, PlayListNodeUploadDto, PlaylistUploadDto, valuesPlayListEdgeDueDateRelativeTo, valuesPlaylistEdgeTransitionFunction } from "collaboration-service";
import ArraySelection from "components/ArraySelection";
import CancelWithConfirmation from "components/CancelWithConfirmation";
import DurationInput from "components/DurationInput/DurationInput";
import DurationInputGetHours from "components/DurationInput/DurationInputGetHours";
import LabelInfo from "components/LabelInfo";
import { EditLanguageStringsBase } from "components/PostEdit/Editors/EditLanguageStrings";
import { defEditorFormats } from "components/PostEdit/TypeImplementations/PostEdit";
import SafeHTML from "components/SafeHTML/SafeHTML";
import { Button, components, DatePicker, fadeColor, GroupBase, Icon, ImgSelect, Input, OptionProps, SingleValueProps, Tooltip, useThemePart } from "imaginarity-react-ui";
import * as _ from "lodash";
import * as React from "react";
import { ApplicationState, useAppSelector } from "services/ApplicationState/ApplicationState";
import { ArrayElement, KeyOfTypeWithOptionals, removeHtmlTags, shallowCompare, visit } from "services/Helpers";
import { sanitizedNothingAllowed, sanitizedNothingAllowedAttributes } from "services/Helpers/SanitizedHelper";
import ScrollHelper from "services/Helpers/ScrollHelper";
import { getTranslated } from "services/Helpers/TranslationHelpers";
import { useImgI18N } from "services/ImgI18N";
import { PlaylistEditState, useSelectorPLE } from "./PlaylistEdit";
import PlaylistEditNodeContents from "./PlaylistEditNodeContents";
import PlaylistEditNodeTitleTooltip from "./PlaylistEditNodeTitleTooltip";
import { playlistEditNodeSchemeColors } from "./PlaylistEditSequenceLinear";
import { PlaylistEditSequenceLinearSC as T } from "./PlaylistEditSequenceLinearSC";
import PlaylistEditTooltip from "./PlaylistEditTooltip";
import { usePLEState } from "./usePLEState";
import { FloatedLeft } from "layout/masterLayouts/MasterLayoutsSC";
import SwitchButton from "components/Switch/SwitchButton";

const BASIC_TRANSITIONS: PlaylistEdgeTransitionFunction[] = ["FallThru", "PercentageDone"];
const valuesPlaylistEdgeTransitionFunctionReduced = _.filter(valuesPlaylistEdgeTransitionFunction, value =>
    BASIC_TRANSITIONS.includes(value as PlaylistEdgeTransitionFunction)
);

export type PlaylistWithFlatNodes = PlaylistUploadDto & { _nodes: PlayListNodeUploadDto[]; }

const mapper = (state: ApplicationState) => ({
    contentLanguage: state.contentLanguage,
});



interface PlaylistProps {
    i: number;
    curNode: string | undefined;
    setCurNode: React.Dispatch<React.SetStateAction<string | undefined>>;
    scrollHelper: ScrollHelper;
    selectedNodeColor?: string;
    setContainerRef: (node: HTMLDivElement | null) => void;
    avlWidth: number;
}

type SC = ArrayElement<typeof playlistEditNodeSchemeColors>;

const SingleValue = (p: SingleValueProps<SC, false, GroupBase<SC>>) => {
    const { data } = p;
    return (
        <components.SingleValue {...p} >
            <T.OptContainer>
                <T.Color color={data.value} />
                <div>{data.label}</div>
            </T.OptContainer>
        </components.SingleValue>
    )
}

const Option = (p: OptionProps<SC, false, GroupBase<SC>>) => {
    const { data } = p;
    return (
        <components.Option {...p} >
            <T.OptContainer>
                <T.Color color={data.value} />
                <div>{data.label}</div>
            </T.OptContainer>
        </components.Option>
    )
}

export const getCurrentPlaylistNode = (s_startNode: any, node: PlayListNodeUploadDto | undefined) => {
    const startNode = s_startNode.clonedValue();
    if (node === undefined)
        return { startNode, curNode: undefined };
    let curNode: PlayListNodeUploadDto | undefined = startNode;
    while (curNode) {
        if (curNode.id === node.id)
            return { startNode, curNode };
        curNode = curNode.children?.[0]?.to;
    }
    return { startNode, curNode: undefined };
};

const pleMapper = (state: PlaylistEditState) => ({
    expertMode: state.expertMode,
    missing: state.missing.phases

});

const PlaylistEditLinearNode = (p: PlaylistProps) => {
    const { i, setCurNode, curNode,
        scrollHelper, selectedNodeColor, setContainerRef, avlWidth,
    } = p;
    const { t } = useImgI18N("playlist");
    const { contentLanguage } = useAppSelector(mapper, shallowCompare);
    const { expertMode, missing } = useSelectorPLE(pleMapper, shallowCompare);
    const theme = useThemePart(t => t);

    const s_startNode = usePLEState(x => x.startNode)

    const nodes: PlayListNodeUploadDto[] = [];
    visit(s_startNode.value, (cur) => _.compact(_.map(cur.children, edge => edge.to)), cur => nodes.push(cur));
    const node = nodes[i];
    const [isOpened, setIsOpened] = React.useState(true);

    // const { errorNode, error } = React.useMemo(() => {
    //     const nodes = playlist._nodes
    //     const node = nodes[i];
    //     const errorNode: ErrorLinearNode = {};
    //     const successor = (node.children ?? [])[0];
    //     if (!successor) {
    //         errorNode.successor = true;
    //         //return errorNode;
    //     }

    //     const { headlines, color } = node;
    //     const referenceIds = node.referenceIds;
    //     const successorReferenceValue = successor?.referenceValue;
    //     errorNode.missingDuration = node.durationInMinutes === undefined || node.durationInMinutes === 0;
    //     errorNode.missingRelativeDueDate = node.dueDateRelativeTo === undefined ? false : ((node.dueDateRelativeTo === "NodeAssignment" || node.dueDateRelativeTo === "NodeFirstItemStart" || node.dueDateRelativeTo === "PlaylistAssignment") && (node.dueDateRelativeInHours === undefined || node.dueDateRelativeInHours === 0));
    //     errorNode.missingAbsoluteDueDate = node.dueDateRelativeTo === undefined ? false : (node.dueDateRelativeTo === "Absolute" && (node.absDueDate === undefined || node.absDueDate === null));
    //     errorNode.missingDueDateRelativeTo = node.dueDateRelativeTo === null || node.dueDateRelativeTo === undefined;

    //     if (referenceIds?.length === 0) {
    //         errorNode.missingNode = true;
    //     }

    //     if (successor?.transitionFunction === "ReferenceDone" || successor?.transitionFunction === "ReferenceDoneWithValue") {
    //         const referenceContentIds: string[] = [];
    //         if (node?.children) {
    //             if (node?.children[0].referenceContentId)
    //                 referenceContentIds.push(node?.children[0].referenceContentId)
    //             if (node?.children[0].referenceContentIds)
    //                 referenceContentIds.push(...node?.children[0].referenceContentIds)
    //         }
    //         if (referenceContentIds.length === 0) {
    //             errorNode.missingNode = true;
    //             //return errorNode;
    //         }

    //         let err = true;
    //         _.forEach(node.referenceIds, refId => {
    //             const pc = _.find(posts, c => c.id === refId);
    //             if (pc && _.find(referenceContentIds, x => x === pc.id) !== undefined)
    //                 err = false;
    //         });
    //         if (err) {
    //             errorNode.missingNode = true;
    //         }
    //     }

    //     if ((successor?.transitionFunction === "ReferenceDoneWithValue" || successor?.transitionFunction === "PercentageDone" || successor?.transitionFunction === "AbsCountDone")
    //         && (successorReferenceValue === undefined || successorReferenceValue === 0 || Number.isNaN(successorReferenceValue))) {
    //         errorNode.successorReferenceValue = true;
    //     }
    //     if (headlines.length === 0) {
    //         errorNode.title = true;
    //     }
    //     if (!color) {
    //         errorNode.color = true;
    //     }

    //     if (!referenceIds || referenceIds.length === 0) {
    //         errorNode.referenceIds = true;
    //     }
    //     if ((successorReferenceValue !== undefined && successor?.transitionFunction === "AbsCountDone") && referenceIds && successorReferenceValue > referenceIds.length) {
    //         errorNode.absCountDone = true;
    //     }

    //     let numberTooHigh = (successorReferenceValue && node?.referenceIds && successor?.transitionFunction === "AbsCountDone")
    //         ? successorReferenceValue > (node.referenceIds?.length || 0)
    //         : ((successorReferenceValue === undefined || successorReferenceValue === 0 || Number.isNaN(successorReferenceValue)) ? true : false);

    //     if (successor?.transitionFunction === "ReferenceDoneWithValue") {
    //         numberTooHigh = successorReferenceValue === undefined ? true : ((successorReferenceValue > 0 && successorReferenceValue <= 100) ? false : true);
    //     }
    //     if (successor?.transitionFunction === "PercentageDone") {
    //         numberTooHigh = successorReferenceValue === undefined ? true : ((successorReferenceValue > 0 && (successorReferenceValue * 100) <= 100) ? false : true);
    //     }
    //     if (successor?.transitionFunction === "FallThru") {
    //         numberTooHigh = false;
    //     }

    //     if (numberTooHigh)
    //         errorNode.numberTooHigh = true;

    //     var error = false;
    //     _.forEach(Object.keys(errorNode), (key: any) => {
    //         if ((errorNode as any)[key]) {
    //             error = true;
    //         }
    //     });
    //     setErrorNodes(prev => ({ ...prev, [node.id ?? ""]: error ? "x" : undefined }));
    //     return { errorNode, error };
    // }, [posts, setErrorNodes, playlist, i]);

    const { nodeUp, nodeDown } = React.useMemo(() => {
        const nodes: PlayListNodeUploadDto[] = [];
        visit(s_startNode.value, (cur) => _.compact(_.map(cur.children, edge => edge.to)), cur => nodes.push(cur));

        const moveNode = (from: number, to: number) => {
            const a = Math.min(Math.max(0, from), nodes.length - 1);
            const b = Math.min(Math.max(0, to), nodes.length - 1);
            if (a === b) return undefined;

            const replaceNodeContent = (node: PlayListNodeUploadDto, by: PlayListNodeUploadDto) => {
                const c = node.children;
                let prop: keyof typeof node;
                for (prop in node) {
                    if (prop !== "id" && prop !== "children")
                        (node as any)[prop] = by[prop];
                }
                node = { ...by, id: node.id, children: c };
            }

            const walkThruNodes = (curNode: PlayListNodeUploadDto | undefined, n1: PlayListNodeUploadDto, n2: PlayListNodeUploadDto) => {
                if (!curNode)
                    return;
                if (curNode.id === n1.id) {
                    // replace with n2
                    //console.log("replace with n2", curNode.id);
                    replaceNodeContent(curNode, n2);
                }
                if (curNode.id === n2.id) {
                    // replace with n1
                    //console.log("replace with n1", curNode.id);
                    replaceNodeContent(curNode, n1);
                }
                _.forEach(curNode.children, c => walkThruNodes(c.to, n1, n2));
            }

            return () => {
                let sNode = s_startNode.clonedValue();
                walkThruNodes(sNode, nodes[a], nodes[b]);
                s_startNode.setValue(sNode);
            };
        }
        return { nodeUp: moveNode(i, i - 1), nodeDown: moveNode(i + 1, i) };
    }, [i, s_startNode]);



    const updateNode = React.useCallback(<T extends any, S extends T>(what: KeyOfTypeWithOptionals<PlayListNodeUploadDto, T>, convert?: (val: S) => T) => (newData: S | ((val: S) => S)) => {
        const { startNode, curNode } = getCurrentPlaylistNode(s_startNode, node);;
        if (!curNode)
            return;
        const val = newData instanceof Function ? newData(curNode[what] as S) : newData;
        if (!convert) {
            (curNode as any)[what] = val;
        } else {
            const tmp: T = convert(val);
            (curNode as any)[what] = tmp;
        }

        if (what === "dueDateRelativeTo") {
            if (val === "None") {
                node.dueDateRelativeInHours = undefined;
                node.absDueDate = undefined;
            }
            if (val === "Absolute") {
                node.dueDateRelativeInHours = undefined;
            } else {
                node.absDueDate = undefined;
            }
        }
        s_startNode.setValue(startNode, true);
    }, [node, s_startNode]);
    const sel = React.useMemo(() => node.id === curNode, [node.id, curNode]);

    // setIsOpened ersetzen

    React.useEffect(() => {
        setIsOpened(o => sel && !o ? true : o);
    }, [sel, setIsOpened]);

    const delNode = React.useCallback(() => {
        const { curNode, startNode } = getCurrentPlaylistNode(s_startNode, node);
        if (!curNode)
            return;
        if (startNode.id === node.id) {
            s_startNode.setValue(curNode.children?.[0]?.to);
        } else {
            let prev: PlayListNodeUploadDto | undefined;
            let cur: PlayListNodeUploadDto | undefined;
            cur = startNode;
            while (cur) {
                if (cur.id === curNode.id && prev) {
                    const children = prev.children ?? [];
                    if (children) {
                        children[0].to = (cur.children ?? [])[0]?.to;
                    }
                    break;
                }
                prev = cur;
                cur = (cur.children ?? [])[0]?.to;
            }
            s_startNode.setValue(startNode);
        }
    }, [s_startNode, node]);

    // Memoized derived values
    const { successor, nodeId, hasEnGbTitleEntry } = React.useMemo(() => {
        const successor = (node.children ?? [])[0];
        const nodeId = node?.id ?? 'index_' + i.toString();
        const hasEnGbTitleEntry = node?.headlines?.some(n => n.lng === "en-GB");
        return { successor, nodeId, hasEnGbTitleEntry };
    }, [node, i]);


    const reduceTransFunctionItems = React.useMemo(() => {
        const firstChild = _.first(node.children)?.transitionFunction;
        const showExpertTransitions = BASIC_TRANSITIONS.includes(firstChild as PlaylistEdgeTransitionFunction);
        return expertMode ? false : showExpertTransitions;
    }, [node, expertMode]);

    // updateTransition, toggleReferenceId, updateReferenceValue, handleNodeClick, updateNode, nodeUp/nodeDown, errorNode bleiben wie bisher memoisiert


    const { updateTransition, toggleReferenceId, updateReferenceValue, toggleByTrainerReferenceId } = React.useMemo(() => {
        const { startNode, curNode } = getCurrentPlaylistNode(s_startNode, node);
        return {
            updateTransition: (newData: string) => {
                if (!curNode)
                    return;
                curNode.children![0].transitionFunction = newData as PlaylistEdgeTransitionFunction;
                if (newData === "AbsCountDone") {
                    curNode.children![0].referenceValue = 1;
                } else if (newData === "PercentageDone") {
                    curNode.children![0].referenceValue = 1;
                } else if (newData === "FallThru") {
                    curNode.children![0].referenceValue = undefined;
                    curNode.dueDateRelativeTo = "None";
                    curNode.children![0].referenceContentIds = undefined;
                }
                s_startNode.setValue(startNode);
            },
            toggleReferenceId: (id: string) => {
                if (!curNode)
                    return;
                if (!curNode.children![0].referenceContentIds)
                    curNode.children![0].referenceContentIds = [];
                if (curNode.children![0].referenceContentId)
                    curNode.children![0].referenceContentIds.push(curNode.children![0].referenceContentId);
                curNode.children![0].referenceContentId = undefined;
                curNode.children![0].referenceContentIds = _.uniq(curNode.children![0].referenceContentIds);
                const idx = _.findIndex(curNode.children![0].referenceContentIds, x => x === id);
                if (idx >= 0)
                    curNode.children![0].referenceContentIds.splice(idx, 1);
                else
                    curNode.children![0].referenceContentIds.push(id);
                curNode.children![0].referenceContentIds = _.uniq(curNode.children![0].referenceContentIds);
                s_startNode.setValue(startNode);
            },
            toggleByTrainerReferenceId: (id: string) => {
                if (!curNode)
                    return;
                if (curNode.byTrainerReferenceIds === undefined)
                    curNode.byTrainerReferenceIds = [];
                const idx = _.findIndex(curNode.byTrainerReferenceIds, x => x === id);
                if (idx >= 0)
                    curNode.byTrainerReferenceIds.splice(idx, 1);
                else
                    curNode.byTrainerReferenceIds.push(id);
                curNode.byTrainerReferenceIds = _.uniq(curNode.byTrainerReferenceIds);
                s_startNode.setValue(startNode);
            },
            updateReferenceValue: (factor: number) => (newData?: string) => {
                if (!curNode)
                    return;
                curNode.children![0].referenceValue = Number.parseFloat(newData ?? "0") / factor;
                s_startNode.setValue(startNode);
            }
        }
    }, [node, s_startNode]);

    const handleNodeClick = React.useMemo(() => (index: number, isOpened: boolean, sel: boolean, nodeId?: string) => {
        if (isOpened) {
            if (nodeId) {
                setCurNode(nodeId);
            }
        } else if (sel) {
            if (nodeId) {
                scrollHelper.scrollTo(nodeId)
            }
        } else {
            if (nodeId) {
                setCurNode(nodeId);
                scrollHelper.scrollTo(nodeId)
            }
        }
    }, [setCurNode, scrollHelper]);


    const referenceIds: string[] = [];
    if ((node.children?.[0]?.transitionFunction === "ReferenceDone" ||
        node.children?.[0]?.transitionFunction === "ReferenceDoneWithValue")) {
        if (node.children?.[0]?.referenceContentId)
            referenceIds.push(node.children[0].referenceContentId);
        if (node.children?.[0]?.referenceContentIds)
            referenceIds.push(...node.children[0].referenceContentIds);
    }

    const nodeMissing = missing.nodes[node.id ?? ""] ?? {};
    const error = _.some(nodeMissing, x => x === true);
    return (
        <React.Fragment key={node.id}>
            <T.NodeContainer
                color={node.color}
                selected={sel}
                ref={scrollHelper.getRef(nodeId)}
                hasMissingElements={error}
            >
                {sel && <T.NodeConnectorH color={selectedNodeColor ?? "@middleLightGrey"} />}
                <T.NodeHeader sel={sel} color={node.color ?? "color"} showNode={isOpened}>
                    <div>
                        <Button
                            floated="left"
                            icon={sel ? "circle dot" : "circle"}
                            content={t("select phase")}
                            kind="halfTransparentButton"
                            disabled={isOpened && sel}
                            onClick={() => setCurNode(node.id)}
                        />
                        {(error) &&
                            <Button kind="fullTransparentButton" iconColor="@accentRed" icon="exclamation triangle" floated="left" tooltip={{ tooltipText: t("missing inputs") }} />
                        }
                    </div>
                    <T.NodeHeaderTitleDiv onClick={() => handleNodeClick(i, isOpened, sel, node.id)}>
                        {!isOpened && node.color && <T.NodeHeaderDot color={node.color} />}
                        <T.NodeHeaderDotDiv>
                            <SafeHTML html={getTranslated(node.headlines, contentLanguage)?.text ?? ""} allowedTags={sanitizedNothingAllowed} allowedAttributes={sanitizedNothingAllowedAttributes} />
                        </T.NodeHeaderDotDiv>
                    </T.NodeHeaderTitleDiv>
                    <div>

                        {sel && isOpened && (
                            <CancelWithConfirmation
                                icon="delete"
                                iconColor="@accentRed"
                                cancel={delNode}
                                confirmText={t("yes, delete phase")}
                                kind="transparentButton"
                                floated="left"
                                iconOnly
                                tooltipInitialButton={t("delete phase")}
                            />
                        )}
                        {nodeUp &&
                            <Button icon="chevron up double"
                                onClick={nodeUp}
                                kind="transparentButton"
                                tooltip={{ tooltipText: t("move phase up"), position: "top" }}
                            />
                        }
                        {nodeDown &&
                            <Button icon="chevron down double"
                                onClick={nodeDown}
                                kind="transparentButton"
                                tooltip={{ tooltipText: t("move phase down"), position: "top" }}
                            />
                        }
                    </div>

                    <div>
                        <Button
                            floated="right"
                            tooltip={{ tooltipText: isOpened ? t("hide phase settings") : t("show phase settings"), position: "top" }}
                            icon={isOpened ? "chevron up" : "chevron down"}
                            kind="halfTransparentButton"
                            onClick={() => setIsOpened(!isOpened)}
                        />
                        {!isOpened && node.referenceIds && (
                            <T.NodeBarInfo onClick={() => setCurNode(node.id)}>
                                <Tooltip tooltipText={t("phase contains {{count}} elements", { count: node.referenceIds.length })} noMargin notInline position="top">
                                    <>{node.referenceIds.length}</>
                                </Tooltip>
                            </T.NodeBarInfo>
                        )}
                    </div>
                </T.NodeHeader>
                {isOpened &&
                    <T.NodeContent onClick={() => setCurNode(node.id)} style={{ marginTop: 5 }}>
                        <T.NodeTitle missing={nodeMissing.headlines} style={{ marginTop: -6, marginBottom: -20, paddingTop: 10 }}>
                            {(node?.headlines ?? []).length > 0 && (
                                <LabelInfo
                                    infoText=""
                                    warning={false}
                                    tooltipPosition="right"
                                    tooltipContent={<PlaylistEditNodeTitleTooltip
                                        data={node?.headlines || []}
                                        removeHtmlTags={removeHtmlTags}
                                        hasEnGbTitleEntry={hasEnGbTitleEntry}
                                        t={t}
                                    />}
                                />
                            )}
                            {t("title")}
                            {nodeMissing.headlines &&
                                <Icon name="asterisk" color={"@accentRed"} style={{ marginTop: 6, marginLeft: 3, marginRight: 5, float: "right" }} />
                            }
                        </T.NodeTitle>
                        <T.NodeText style={{ marginTop: -6, marginBottom: -6, gridColumnEnd: 4, paddingRight: 12, minWidth: 0 }} ref={setContainerRef}>
                            <EditLanguageStringsBase
                                value={node.headlines ?? []}
                                setValue={updateNode("headlines", (x) => x ?? [])}
                                editMode={true}
                                editorFormats={[]}
                                type="headlines"
                                noHtml
                                asInput
                                hideToolbar
                                fixWidth={avlWidth}
                                initialOpen={(node?.headlines ?? []).length === 0}
                                hideTitleBarIfEmpty
                                savingText={t('confirm title')}
                                savingIcon='check'
                            />
                        </T.NodeText>

                        {expertMode &&
                            <>
                                <T.NodeTitle style={{ marginTop: -10, marginBottom: -6, paddingTop: 10 }}>
                                    <LabelInfo
                                        infoText=""
                                        warning={false}
                                        tooltipPosition="right"
                                        tooltipContent={<PlaylistEditNodeTitleTooltip
                                            data={node?.descriptions || []}
                                            removeHtmlTags={removeHtmlTags}
                                            hasEnGbTitleEntry={hasEnGbTitleEntry}
                                            t={t}
                                            msg={node?.descriptions && node?.descriptions.length > 0 ?
                                                t("descriptions are translated into the languages listed. if a language lacks a description, english or the first entered language is used as a fallback.")
                                                :
                                                t("phase descriptions are optional, no descriptions have been entered for any language yet.")
                                            }
                                        />
                                        }
                                    />
                                    {t("description")}
                                </T.NodeTitle>
                                <T.NodeTitleDescription style={{ marginTop: -10, marginBottom: -6, gridColumnEnd: 4, paddingRight: 10 }}>
                                    {/* <Editor
                                                                                value={getTranslated(node.descriptions, contentLanguage)?.text ?? ""}
                                                                                onChange={updateDescriptionsX}
                                                                            /> */}
                                    <EditLanguageStringsBase
                                        value={node.descriptions ?? []}
                                        setValue={updateNode("descriptions", (x) => x ?? [])}
                                        editMode={true}
                                        editorFormats={defEditorFormats["StandardDescription"]}
                                        type="descriptions"
                                        fixWidth={avlWidth}
                                        initialOpen={node.descriptions?.length === 0}
                                        hideTitleBarIfEmpty
                                        savingText={t('confirm description')}
                                        savingIcon='check'
                                    />
                                </T.NodeTitleDescription>

                                <T.NodeTitle missing={nodeMissing.color}>
                                    <LabelInfo
                                        infoText=""
                                        warning={nodeMissing.color}
                                        icon={nodeMissing.color ? "asterisk" : undefined}
                                        tooltipPosition="right"
                                        tooltipContent={
                                            <PlaylistEditTooltip
                                                title={t("phase accent color")}
                                                paragraphs={[t("set the accent colour for this phase.")]}
                                            />
                                        }
                                    />
                                    {t("phase accent color")}
                                </T.NodeTitle>
                                <T.NodeInput>
                                    <ImgSelect
                                        options={playlistEditNodeSchemeColors}
                                        onChange={updateNode("color", c => c === null ? undefined : c.value)}
                                        value={_.find(playlistEditNodeSchemeColors, s => s.value === node.color)}
                                        isClearable
                                        isSearchable={false}
                                        components={{
                                            Option: Option,
                                            SingleValue: SingleValue,
                                        }}
                                    />
                                </T.NodeInput>
                            </>
                        }

                        <T.NodeTitle missing={nodeMissing.missingDuration}>
                            <LabelInfo
                                warning={nodeMissing.missingDuration}
                                icon={nodeMissing.missingDuration ? "asterisk" : undefined}
                                infoText=""
                                tooltipPosition="right"
                                tooltipContent={
                                    <PlaylistEditTooltip
                                        title={t("estimated duration")}
                                        paragraphs={[t("how long do you estimate is the duration for this phase (one day equals 8 hours)?")]}
                                    />
                                }
                            />
                            {t("estimated duration")}
                        </T.NodeTitle>
                        <T.NodeInput>
                            <DurationInput
                                value={node.durationInMinutes ?? 0}
                                durationFromInput={updateNode("durationInMinutes")}
                                step={5}
                                cleareable
                                maximized
                                debounce={500}
                            />
                        </T.NodeInput>


                        {(node.children ?? []).length > 0 &&
                            <>
                                <T.NodeTitle >
                                    <LabelInfo
                                        infoText=""
                                        tooltipPosition="right"
                                        tooltipContent={
                                            <PlaylistEditTooltip
                                                paragraphs={[
                                                    <><span style={{ fontWeight: "bolder" }}>{t("fallThru")}</span>:<br />{t("the next phase is displayed automatically.")}</>,
                                                    <><span style={{ fontWeight: "bolder" }}>{t("percentageDone")}</span>:<br />{t("the next phase is only displayed when a set percentage has been completed.")}</>,
                                                    <><span style={{ fontWeight: "bolder" }}>{t("absCountDone")}</span> <Icon name="asterisk" color="@accentRed" size={12} />:<br />{t("the next phase is only displayed when at least the set number of elements has been completed.")}</>,
                                                    <><span style={{ fontWeight: "bolder" }}>{t("referenceDone")}</span> <Icon name="asterisk" color="@accentRed" size={12} />:<br />{t("the next phase is only displayed when a referenced element has been completed.")}</>,
                                                    <><span style={{ fontWeight: "bolder" }}>{t("referenceDoneWithValue")}</span> <Icon name="asterisk" color="@accentRed" size={12} />:<br />{t("the next phase is only displayed when a referenced element has been completed at least to the set percentage.")}</>,
                                                    <span style={{ marginTop: 10, color: "#950014", fontSize: "0.9em" }}>{t("transition functions marked with * are only visible in expert mode")}</span>
                                                ]}
                                            />
                                        }
                                    />
                                    {t("transition function")}
                                </T.NodeTitle>
                                <T.NodeInput style={{ position: "relative" }}>
                                    <ArraySelection
                                        array={reduceTransFunctionItems ? valuesPlaylistEdgeTransitionFunctionReduced : valuesPlaylistEdgeTransitionFunction}
                                        value={successor.transitionFunction}
                                        isMulti={false}
                                        namespace='playlist'
                                        onChange={updateTransition}
                                    />
                                </T.NodeInput>

                                {successor.transitionFunction !== "ReferenceDone" && successor.transitionFunction !== "FallThru" && successor.transitionFunction !== "TrainerAction" &&
                                    <>
                                        <T.NodeTitle missing={nodeMissing.numberTooHigh}>
                                            <LabelInfo
                                                infoText=""
                                                warning={nodeMissing.numberTooHigh}
                                                icon={nodeMissing.numberTooHigh ? "asterisk" : undefined}
                                                tooltipPosition="right"
                                                tooltipContent={
                                                    <PlaylistEditTooltip>
                                                        <div style={{ display: "grid", gridTemplateColumns: "max-content 1fr", columnGap: 10 }}>
                                                            <div>{t("min value")}:</div>
                                                            <div style={{ textAlign: "right" }}><b>{successor.transitionFunction === "AbsCountDone" ? 1 : 5}</b></div>
                                                            <div>{t("max value")}:</div>
                                                            <div style={{ textAlign: "right" }}><b>{successor.transitionFunction === "AbsCountDone" ? node.referenceIds?.length : 100}</b></div>
                                                        </div>
                                                    </PlaylistEditTooltip>
                                                }

                                            />
                                            {(successor.transitionFunction === "ReferenceDoneWithValue" || successor.transitionFunction === "PercentageDone") ? t("reference value [%]") : t("reference value")}
                                        </T.NodeTitle>
                                        <T.NodeInput>
                                            <Input
                                                inline
                                                width="100%"
                                                min={successor.transitionFunction === "AbsCountDone" ? 1 : 5}
                                                max={successor.transitionFunction === "AbsCountDone" ? node.referenceIds?.length : 100}
                                                step={successor.transitionFunction === "AbsCountDone" ? 1 : 5}
                                                value={Math.round((successor.referenceValue ?? 0) * (successor.transitionFunction === "PercentageDone" ? 100 : 1))}
                                                debounce={500}
                                                type="number"
                                                change={updateReferenceValue((successor.transitionFunction === "PercentageDone" ? 100 : 1))}
                                            />
                                        </T.NodeInput>
                                    </>
                                }
                            </>

                        }
                        <T.NodeTitle missing={nodeMissing.missingDueDateRelativeTo}>
                            <LabelInfo
                                warning={nodeMissing.missingDueDateRelativeTo}
                                icon={nodeMissing.missingDueDateRelativeTo ? "asterisk" : undefined}
                                infoText=""
                                tooltipPosition="right"
                                tooltipContent={
                                    <PlaylistEditTooltip
                                        paragraphs={[
                                            <><span style={{ fontWeight: "bolder" }}>{t("Absolute")}</span>:<br />{t("by which date this phase must be completed.")}</>,
                                            <><span style={{ fontWeight: "bolder" }}>{t("NodeAssignment")}</span>:<br />{t("in how many hours must the phase be completed depending on the phase assignment?")}</>,
                                            <><span style={{ fontWeight: "bolder" }}>{t("PlaylistAssignment")}</span>:<br />{t("in how many hours must the phase be completed depending on the playlist assignment?")}</>,
                                            <><span style={{ fontWeight: "bolder" }}>{t("NodeFirstItemStart")}</span>:<br />{t("in how many hours must the phase be completed depending on the first item start assignment?")}</>
                                        ]}
                                    />
                                }
                            />
                            {t("due date relative to")}
                        </T.NodeTitle>
                        <T.NodeInput>
                            <ArraySelection
                                array={valuesPlayListEdgeDueDateRelativeTo}
                                namespace='playlist'
                                isMulti={false}
                                onChange={updateNode("dueDateRelativeTo")}
                                value={node.dueDateRelativeTo ?? "PlaylistAssignment"}
                            />

                        </T.NodeInput>
                        {node.dueDateRelativeTo !== "None" &&
                            <>
                                {node.dueDateRelativeTo === "Absolute" ?
                                    <>
                                        <T.NodeTitle style={{ marginBottom: -15 }} missing={nodeMissing.missingAbsoluteDueDate}>
                                            {t("due date")}
                                            {nodeMissing.missingAbsoluteDueDate &&
                                                <>
                                                    <Icon name="asterisk" color={"@accentRed"} style={{ marginTop: 6, marginLeft: 3, marginRight: 5, float: "right" }} />
                                                </>
                                            }
                                        </T.NodeTitle>
                                        <T.NodeDate >
                                            <DatePicker
                                                dateFormat="P"
                                                value={node.absDueDate ? new Date(node.absDueDate) : undefined}
                                                onChange={updateNode("absDueDate")}
                                                noMargin
                                                width="100%"
                                                showTodayText={t("today")}
                                                showYearDropdown
                                                showMonthDropdown
                                                isClearable

                                            />

                                        </T.NodeDate>
                                    </>
                                    :
                                    <>
                                        <T.NodeTitle missing={nodeMissing.missingRelativeDueDate}>
                                            <LabelInfo
                                                infoText=""
                                                warning={nodeMissing.missingRelativeDueDate}
                                                icon={nodeMissing.missingRelativeDueDate ? "asterisk" : undefined}
                                                tooltipPosition="right"
                                                tooltipContent={
                                                    <PlaylistEditTooltip
                                                        title={t("relative due date")}
                                                        paragraphs={[t("after which time the phase must be completed depending on {{phase}} (one day equals 24 hours)?", { phase: node.dueDateRelativeTo })]}
                                                    />
                                                }
                                            />
                                            {t("relative due date")}
                                        </T.NodeTitle>
                                        <T.NodeInput>
                                            <DurationInputGetHours
                                                durationFromInput={updateNode("dueDateRelativeInHours")}
                                                maximized
                                                value={node.dueDateRelativeInHours ?? undefined}
                                                cleareable
                                                debounce={500}

                                            />

                                        </T.NodeInput>
                                    </>
                                }
                            </>
                        }
                        {i > 0 &&
                            <>
                                <T.NodeTitle style={{ marginBottom: -10 }}>
                                    <LabelInfo
                                        infoText=""
                                        tooltipPosition="right"
                                        tooltipContent={
                                            <PlaylistEditTooltip
                                                title={t("visibility")}
                                                paragraphs={[t("if activated, the user can see the elements of the phase that has not yet been activated in the detailed view of the playlist")]}
                                            />
                                        }
                                    />
                                    {t("preview")}
                                </T.NodeTitle>
                                <T.NodeTextDiv >
                                    <FloatedLeft margin="5px 0 0 0" >
                                        <SwitchButton
                                            isActive={() => updateNode("visible")(!node.visible)}
                                            initialState={node.visible ?? false}
                                            activeColor={node.color ? fadeColor({ theme }, node.color, 15) : "@alertInfoInformation"}
                                            activeDotColor="@darkGrey"
                                        />
                                    </FloatedLeft>
                                    <T.NodeHeaderDotDiv style={{ marginLeft: 10, marginTop: 10 }}>{t("preview elements in detail view are {{vis}}", { vis: node.visible === true ? "shown" : "hidden (standard)" })}</T.NodeHeaderDotDiv>
                                </T.NodeTextDiv>
                            </>
                        }

                        <T.NodeTitle missing={nodeMissing.missingNode || node.referenceIds?.length === 0}>
                            <T.NodeTitleDiv>
                                <LabelInfo
                                    infoText=""
                                    warning={nodeMissing.missingNode}
                                    icon={nodeMissing.missingNode ? "asterisk" : undefined}
                                    tooltipPosition="right"
                                    tooltipContent={
                                        <PlaylistEditTooltip
                                            title={t("phase's content")}
                                            paragraphs={[
                                                successor?.transitionFunction === "ReferenceDone"
                                                    ? t("at least one element from the left list (Implemented Elements) must be set and also referenced.")
                                                    : t("at least one element from the list on the left (Implemented Elements) must be set.")
                                            ]}
                                        />
                                    }
                                />
                                {t("phase's content")}
                            </T.NodeTitleDiv>
                        </T.NodeTitle>
                        <T.NodeTextHeightDiv hasError={node.referenceIds?.length === 0}>
                            {node.referenceIds?.length === 0 && <div><Icon name="exclamation" color="@accentRed" style={{ float: "left", marginRight: 10 }} marginTop={4} />{t("no element selected")}</div>}
                            {node.referenceIds && node.referenceIds.length > 0 && <div>{t("phase contains {{count}} elements", { count: node.referenceIds.length })}</div>}
                        </T.NodeTextHeightDiv>
                        <PlaylistEditNodeContents
                            node={node}
                            referenceIds={referenceIds}
                            successor={successor}
                            sel={sel}
                            toggleReferenceId={toggleReferenceId}
                            toggleByTrainerReferenceId={toggleByTrainerReferenceId}
                        />

                    </T.NodeContent>
                }

            </T.NodeContainer>
            {i < nodes.length - 1 &&
                <T.NodeConnectorV>
                    <Icon name="arrow down" style={{ position: "absolute", top: "50%", left: "50%", transform: "scaleY(1.2) translate(-50%, -50%)" }} color="@middleLightGrey" />
                </T.NodeConnectorV>
            }
        </React.Fragment>
    )
}

// PlaylistEditLinearNode.whyDidYouRender = {
//     logOnDifferentValues: true,
// }

export default PlaylistEditLinearNode;